import type { SnackBarConfig } from './Snackbar';
import { Box, Button } from '@mui/material';
import React, { useContext, useRef, useState } from 'react';
import { trackEvent } from '@/third-party/ga';
import { UserContext } from '@/shared/contexts/UserContext';
import { useApi } from '../services/api';
import {
  ButtonContainer,
  ModalContent,
  ModalHeader,
  ModalText,
  StyledModal,
} from '../styles/ModalStyles';
import { useExtendState } from '../utils/ExtendState';
import Input from '@/shared/components/Input';
import CustomSnackbar from './Snackbar';
import { Textarea } from './TextArea';

export enum EmailType {
  INVITE = 'invite',
  SHARE_REVIEW = 'share_review',
}

interface SendEmailFormData {
  email: string;
  sentFromEmail?: string;
  recipientName?: string;
  message?: string;
  fromName?: string;
}

interface SendEmailModalProps {
  isOpen: boolean;
  handleClose: () => void;
  emailType?: EmailType;
  title?: string;
  subtitle?: string;
  subtext?: string;
  companyName?: string;
  reviewId?: number;
}

const SendEmailModal: React.FC<SendEmailModalProps> = ({
  isOpen,
  handleClose,
  emailType = EmailType.INVITE,
  reviewId = 0,
  companyName = '',
  title = 'Invite New User',
  subtitle = 'Enter the email address, name, and an optional message for the person you\'d like to invite.',
  subtext = 'They\'ll receive an email with your message and an invitation link.',
}) => {
  const { user } = useContext(UserContext);
  const [snackbarConfig, setSnackbarConfig] = useState<SnackBarConfig>();
  const api = useApi();

  const [formData, extendFormData, setFormData]
    = useExtendState<SendEmailFormData>({
      email: '',
      recipientName: '',
      message: '',
      fromName: user?.firstName ? `${user.firstName} ${user.lastName}` : '',
      sentFromEmail: user?.email || '',
    });
  const { email, recipientName, message } = formData;

  const formRef = useRef<HTMLFormElement>(null);

  const handleSnackBarClose = () => {
    setSnackbarConfig({ ...snackbarConfig, open: false });
  };

  const shareReview = async () => {
    trackEvent({
      eventName: 'share_review',
      eventCategory: companyName,
      eventValue: reviewId,
    });

    const input = {
      ...formData,
      sentFromEmail: user?.email || '',
      companyName,
      fromName: user?.firstName ? `${user.firstName} ${user.lastName}` : '',
      reviewId,
    };
    await api.post(
      `${import.meta.env.VITE_API_BASE_URL}/review/share-review`,
      input,
    );
    handleClose();
    setFormData({});
    setSnackbarConfig({
      open: true,
      type: 'success',
      message: 'Deal retro shared successfully.',
    });
  };

  const inviteNewUser = async () => {
    const input = {
      ...formData,
      sentFromEmail: user?.email || '',
      fromName: user?.firstName ? `${user.firstName} ${user.lastName}` : '',
    };
    await api.post(
      `${import.meta.env.VITE_API_BASE_URL}/admin/user/invite`,
      input,
    );
    handleClose();
    setFormData({});
    setSnackbarConfig({
      open: true,
      type: 'success',
      message: 'Invitation email sent successfully.',
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const form = formRef.current!;
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    if (emailType === EmailType.SHARE_REVIEW) {
      shareReview();
      return;
    }

    if (emailType === EmailType.INVITE) {
      inviteNewUser();
    }
  };

  return (
    <>
      <StyledModal
        open={isOpen}
        onClose={handleClose}
        aria-labelledby="invite-new-user-title"
      >
        <ModalContent>
          <ModalHeader id="invite-new-user-title">{title}</ModalHeader>

          <ModalText>{subtitle}</ModalText>
          <ModalText>{subtext}</ModalText>

          <Box
            component="form"
            ref={formRef}
            noValidate
            onSubmit={handleSubmit}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
            }}
          >
            <Box
              sx={{
                'width': '100%',
                'display': 'flex',
                'flexDirection': 'column',
                'gap': '4px',
                'marginTop': 'var(--spacing-md)',
                '&:first-of-type': {
                  marginTop: 0,
                },
                '& label': {
                  fontSize: '14px',
                  fontWeight: 500,
                },
              }}
            >
              <Input
                required
                fullWidth
                label="Recipient Email"
                type="email"
                name="email"
                currentValue={email}
                onChange={e => extendFormData({ email: e.target.value })}
              />
            </Box>

            <Box
              sx={{
                'width': '100%',
                'display': 'flex',
                'flexDirection': 'column',
                'gap': '4px',
                'marginTop': 'var(--spacing-md)',
                '&:first-of-type': {
                  marginTop: 0,
                },
                '& label': {
                  fontSize: '14px',
                  fontWeight: 500,
                },
              }}
            >
              <Input
                fullWidth
                label="Recipient Name (optional)"
                type="text"
                currentValue={recipientName}
                name="recipientName"
                onChange={e =>
                  extendFormData({ recipientName: e.target.value })}
              />
            </Box>

            <Box sx={{ marginTop: 'var(--spacing-md)' }}>
              <Textarea
                placeholder="Optional message to the recipient"
                minRows={4}
                value={message}
                onChange={e => extendFormData({ message: e.target.value })}
              />
            </Box>

            <ButtonContainer>
              <Button
                type="button"
                onClick={handleClose}
                sx={{
                  'padding': 'var(--spacing-sm) var(--spacing-md)',
                  'borderRadius': '8px',
                  'fontSize': '16px',
                  'backgroundColor': '#f1f1f1',
                  'border': 'none',
                  'cursor': 'pointer',
                  'transition': 'background-color 0.3s ease',
                  '&:hover': {
                    backgroundColor: '#e1e1e1',
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!email}
                sx={{
                  'padding': 'var(--spacing-sm) var(--spacing-md)',
                  'borderRadius': '8px',
                  'fontSize': '16px',
                  'backgroundColor': 'var(--color-primary-light-blue)',
                  'border': 'none',
                  'cursor': 'pointer',
                  'transition': 'background-color 0.3s ease',
                  '&:hover': {
                    backgroundColor: '#adc7f0',
                  },
                }}
              >
                Send Email
              </Button>
            </ButtonContainer>
          </Box>
        </ModalContent>
      </StyledModal>
      <CustomSnackbar config={snackbarConfig} setOpen={handleSnackBarClose} />
    </>
  );
};

export default SendEmailModal;
